import { useMemo } from "react";
import { useI18n } from "../i18n/I18nProvider";
import {
  NAV_FEATURES,
  TOP_STATS,
  SERVICES,
  FEATURES,
  MODULES,
  PAYMENT_CONTROLS,
  STEPS,
  SOLUTIONS,
  PACKAGES,
  SUPPORT,
  FREE_TRIAL,
} from "../mock";

function mergeByIndex(base, overlay, keys) {
  return base.map((item, i) => {
    const o = overlay[i] || {};
    const merged = { ...item };
    keys.forEach((k) => {
      if (o[k] != null) merged[k] = o[k];
    });
    return merged;
  });
}

function mergeNavFeatures(base, overlay) {
  return base.map((col, i) => ({
    ...col,
    group: overlay[i]?.group ?? col.group,
    items: col.items.map((it, j) => ({
      ...it,
      title: overlay[i]?.items?.[j]?.title ?? it.title,
      desc: overlay[i]?.items?.[j]?.desc ?? it.desc,
    })),
  }));
}

function mergePackages(base, overlay) {
  return base.map((pkg, i) => ({
    ...pkg,
    name: overlay[i]?.name ?? pkg.name,
    desc: overlay[i]?.desc ?? pkg.desc,
    tag: overlay[i]?.tag ?? pkg.tag,
    offer: overlay[i]?.offer ?? pkg.offer,
    features: overlay[i]?.features ?? pkg.features,
  }));
}

function mergeSolutions(base, overlay) {
  return base.map((sol, i) => ({
    ...sol,
    title: overlay[i]?.title ?? sol.title,
    lead: overlay[i]?.lead ?? sol.lead,
    points: (overlay[i]?.points || sol.points).map((p, j) => ({
      ...sol.points[j],
      ...p,
    })),
  }));
}

function mergeSupport(base, overlay) {
  return {
    ...base,
    ...overlay,
    stats: base.stats.map((s, i) => ({
      ...s,
      note: overlay.stats?.[i]?.note ?? s.note,
      value: overlay.stats?.[i]?.value ?? s.value,
    })),
    languages: overlay.languages ?? base.languages,
  };
}

/** Localized CMS fallbacks (icons/images from mock.js). */
export function useBookersContent() {
  const { t } = useI18n();
  const c = t("content");

  return useMemo(() => {
    const content = c && typeof c === "object" ? c : {};
    return {
      navFeatures: mergeNavFeatures(NAV_FEATURES, content.navFeatures || []),
      topStats: mergeByIndex(TOP_STATS, content.topStats || [], ["tag", "value", "suffix", "note"]),
      services: mergeByIndex(SERVICES, content.services || [], ["title", "desc"]),
      features: mergeByIndex(FEATURES, content.features || [], ["title", "desc"]),
      modules: mergeByIndex(MODULES, content.modules || [], ["label", "desc"]),
      paymentControls: mergeByIndex(PAYMENT_CONTROLS, content.paymentControls || [], ["title", "desc"]),
      steps: mergeByIndex(STEPS, content.steps || [], ["title", "desc"]),
      results: content.results || t("content.results") || [],
      freeTrial: { ...FREE_TRIAL, ...(content.freeTrial || {}) },
      support: mergeSupport(SUPPORT, content.support || {}),
      faqs: content.faqs || [],
      packages: mergePackages(PACKAGES, content.packages || []),
      solutions: mergeSolutions(SOLUTIONS, content.solutions || []),
      problemBlocks: content.problemBlocks || [],
    };
  }, [c, t]);
}
